import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from '../../../../Flux/hooks.ts'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { kpiMode, useSourceView } from './UseSourceView.ts'
import { useTransformer } from '../Transformer/UseTransformer.tsx'
import { StackedBarChartNext } from '../../../Component/Charts/StackedBarChartNext.tsx'
import { flatStackSeries, type StackSeries } from '../../../Component/Charts/StackSeries.js'
import { OTHER_SERIES_KEY } from './Selector/CollapseSeries.ts'
import { useSeriesSinkTable } from '../DataTable/UseSinkTable.ts'
import { SinkContextRow, TaxaBreadcrumb } from './SinkContextRow.tsx'

export type StackedParams = {
  normalize?: false
  xAxisLabel?: string
  xTickMaxChars?: number
  xTickRotate?: number
}
const EMPTY_PATH: string[] = []
const EMPTY_DATA: unknown[] = []
const EMPTY_SERIES: StackSeries[] = []
const EMPTY_KEYS: string[] = []

export default function StackedBarSinkNext({
  sourceId,
  config
}: {
  sourceId: SourceId
  sinkId: SinkId
  config: SinkConfig<StackedParams>
}) {
  const dispatch = useDispatch()

  const { snapshot, shared, status } = useSourceView(sourceId)

  const selectedPath = shared?.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared?.gradeThreshold ?? 0
  const countMode = shared?.countMode ?? 'Reads'
  const xAxisLabel = config.params?.xAxisLabel ?? 'Groups'

  const scalingMode = shared?.scalingMode ?? 'ABSOLUTE'
  const { inTraitMode, traitGroup, traitName, noTraitMode } = kpiMode(shared)
  // Trait reference data loads async; the single-trait filter only applies once it's resident. Auto-descend
  // must wait for it (see the effect) or it decides against the UNFILTERED cube and misses the descend.
  const traitsLoading = useSelector(
    (s) => s.visualization.traits?.loadingState === 'initializing' || s.visualization.traits?.loadingState === 'loading'
  )

  const seriesRes = inTraitMode
    ? useTransformer(sourceId, undefined, 'traitStackedBarSeries', { selectedPath, gradeThreshold, traitGroup, scalingMode, noTraitMode })
    : useTransformer(sourceId, undefined, 'taxaStackedBarSeries', { selectedPath, gradeThreshold, scalingMode })

  // Auto-descend once (per mount) to the first level where the taxonomy actually branches, so a
  // breakdown chart doesn't open on a single uninformative "Eukaryota" segment (or, when trait- or
  // grade-filtered, a long single lineage above the split). One-shot via the ref so manual Back/Reset
  // is respected; skipped in trait-COMPOSITION mode, where bars are traits and setTaxaZoom doesn't
  // apply. See the taxaAutoDescendTarget transformer for the (compacted-ladder-safe) path it returns.
  const autoDescendRes = useTransformer(sourceId, undefined, 'taxaAutoDescendTarget', { selectedPath, gradeThreshold, scalingMode })
  const didAutoDescendRef = useRef(false)
  useEffect(() => {
    if (didAutoDescendRef.current || inTraitMode) return
    // Only auto-open from ROOT. A non-empty path means the view was deliberately set — by the user drilling,
    // by the agent's directive.selectedPath, or by a restored pinned view — and must never be hijacked. This
    // is also what keeps it robust across REMOUNTS (pinning moves the card to another list, maximize swaps
    // grid↔overlay): the source's path is preserved by createSource, so a remount at a non-root path is left
    // alone instead of re-descending. The per-mount ref then just avoids redundant work at root.
    if (selectedPath.length > 0) return
    // A single-trait FILTER only takes effect once the trait reference data is resident. Deciding before then
    // would evaluate the unfiltered cube (which usually branches at the root) and arm the one-shot without
    // descending — the exact miss for a "chart of just the pollinators". Wait for the trait data first.
    if (traitName && traitsLoading) return
    if (autoDescendRes.status !== 'ready') return
    didAutoDescendRef.current = true
    const target = autoDescendRes.value
    if (target.length > selectedPath.length) {
      dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path: target, auto: true }))
    }
  }, [autoDescendRes, selectedPath, inTraitMode, sourceId, dispatch])

  const { data, stackKeys, stackSeries, sampleCounts } = useMemo(() => {
    if (seriesRes.status !== 'ready') {
      return { data: EMPTY_DATA, stackKeys: EMPTY_KEYS, stackSeries: EMPTY_SERIES, sampleCounts: undefined }
    }
    const { data, stackKeys, sampleCounts } = seriesRes.value
    return { data, stackKeys, stackSeries: flatStackSeries(stackKeys), sampleCounts }
  }, [seriesRes])

  // The numbers behind the bars, published to the container's "Numbers" view. Built from the very
  // series the chart draws -- including the collapsed `Other` segment -- so the two cannot disagree.
  const tableRows = useMemo(
    () =>
      (data as Array<Record<string, number | string>>).map((row, index) => ({
        label: String(row.xAxisvalue ?? ''),
        sampleCount: sampleCounts?.[index],
        values: row as Record<string, number>
      })),
    [data, sampleCounts]
  )

  useSeriesSinkTable({
    sourceId,
    selectedPath,
    gradeThreshold,
    inTraitMode,
    ready: seriesRes.status === 'ready' && tableRows.length > 0,
    categories: stackKeys,
    rows: tableRows,
    groupLabel: xAxisLabel,
    categoryLabel: inTraitMode ? 'Trait group' : 'Taxon',
    valueLabel: scalingMode === 'PERCENTAGE' ? 'Share of group (%)' : String(countMode)
  })

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70 p-3">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (data.length === 0 || stackSeries.length === 0) {
    return <div className="text-sm opacity-70 p-3">No data for current selection.</div>
  }

  const onBarClick = (taxon: string) => {
    if (inTraitMode) return
    if (taxon === OTHER_SERIES_KEY) return // aggregate remainder — not a drillable taxon
    const nextPath = [...selectedPath, taxon]
    dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path: nextPath }))
  }

  const onBack = () => dispatch(visualizationSlice.actions.goBackOneTaxaLevel({ sourceId }))
  const onReset = () => dispatch(visualizationSlice.actions.resetTaxaZoom({ sourceId }))

  return (
    <div className="flex flex-col h-full">
      <SinkContextRow>
        <TaxaBreadcrumb path={selectedPath} onBack={onBack} onReset={onReset} />
      </SinkContextRow>

      <StackedBarChartNext
        dataKey="xAxisvalue"
        data={data}
        stackSeries={stackSeries}
        onBarClick={inTraitMode ? undefined : onBarClick}
        ylabel={String(countMode)}
        xlabel={xAxisLabel}
        height={'100%'}
        sampleCounts={sampleCounts}
        xTickMaxChars={config.params.xTickMaxChars}
        xTickRotate={config.params.xTickRotate}
        isPercent={shared?.scalingMode === 'PERCENTAGE'}
      />
    </div>
  )
}
